import { useEffect, useState } from "react";
import { Mail, MessageSquareText, Phone, RefreshCw, UserRound } from "lucide-react";
import { getQuoteRequestsApi, updateQuoteRequestStatusApi } from "../../app/api";

const statuses = ["NEW", "CONTACTED", "CLOSED"];

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function QuoteRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await getQuoteRequestsApi();
      if (res?.success) setRequests(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    const res = await updateQuoteRequestStatusApi(id, status);
    if (res?.success) {
      setRequests((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status } : item))
      );
    }
    setUpdatingId(null);
  };

  return (
    <div className="min-h-screen rounded-[20px] bg-[#080f0e] px-5 py-8 text-white md:mt-24 md:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-7 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="text-sm font-semibold text-emerald-200">Sales inquiries</p>
            <h1 className="mt-2 text-3xl font-semibold">Quote Requests</h1>
            <p className="mt-2 text-sm text-white/50">
              Requests submitted from the public UPTASK landing page.
            </p>
          </div>
          <button
            type="button"
            onClick={loadRequests}
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.06] px-4 py-2 text-sm font-semibold transition hover:bg-white/10"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-12 text-center text-white/55">
            Loading quote requests...
          </div>
        ) : requests.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.035] p-12 text-center text-white/55">
            No quote requests yet.
          </div>
        ) : (
          <div className="grid gap-4">
            {requests.map((item) => (
              <article
                key={item.id}
                className="rounded-xl border border-white/10 bg-white/[0.045] p-5 shadow-[0_20px_70px_rgba(0,0,0,0.22)]"
              >
                <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                  <div className="min-w-0">
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                      <span className="rounded-lg bg-emerald-300/12 px-3 py-1 text-xs font-bold text-emerald-100">
                        {item.status}
                      </span>
                      <span className="text-xs text-white/42">{formatDate(item.created_at)}</span>
                    </div>
                    <h2 className="flex items-center gap-2 text-xl font-semibold">
                      <UserRound size={18} className="text-emerald-200" />
                      {item.contact_name}
                    </h2>
                    <div className="mt-3 flex flex-wrap gap-3 text-sm text-white/58">
                      <a href={`mailto:${item.email}`} className="inline-flex items-center gap-2 transition hover:text-white">
                        <Mail size={15} />
                        {item.email}
                      </a>
                      {item.phone && (
                        <a href={`tel:${item.phone}`} className="inline-flex items-center gap-2 transition hover:text-white">
                          <Phone size={15} />
                          {item.phone}
                        </a>
                      )}
                    </div>
                  </div>

                  <select
                    value={item.status}
                    disabled={updatingId === item.id}
                    onChange={(event) => updateStatus(item.id, event.target.value)}
                    className="rounded-lg border border-white/10 bg-[#101b19] px-3 py-2 text-sm text-white outline-none transition focus:border-emerald-200/70"
                  >
                    {statuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-5 rounded-lg border border-white/10 bg-black/18 p-4">
                  <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/35">
                    <MessageSquareText size={14} />
                    Query
                  </p>
                  <p className="whitespace-pre-wrap text-sm leading-7 text-white/72">{item.query}</p>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
